import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MapPin, Users, TrendingUp, Calendar, Heart } from 'lucide-react';
import { LANDING_COLORS } from './LandingConstants';

/**
 * AnimatedPitch - Cycles through story scenes with auto-advancement
 * @param {function} t - Translation function from react-i18next
 */
export default function AnimatedPitch({ t }) {
  const [currentScene, setCurrentScene] = useState(0);
  
  const scenes = [
    {
      id: 'hook',
      icon: MapPin,
      text: t('coachLanding.scene1Text'),
      subtext: t('coachLanding.scene1Subtext'),
      color: LANDING_COLORS.primary,
    },
    {
      id: 'problem',
      icon: Users,
      text: t('coachLanding.scene2Text'),
      subtext: t('coachLanding.scene2Subtext'),
      color: '#ef4444',
    },
    {
      id: 'opportunity',
      icon: TrendingUp,
      text: t('coachLanding.scene3Text'),
      subtext: t('coachLanding.scene3Subtext'),
      color: LANDING_COLORS.accent,
    },
    {
      id: 'solution',
      icon: Calendar,
      text: t('coachLanding.scene4Text'),
      subtext: t('coachLanding.scene4Subtext'),
      color: '#3b82f6',
    },
    {
      id: 'value',
      icon: Heart,
      text: t('coachLanding.scene5Text'),
      subtext: t('coachLanding.scene5Subtext'),
      color: '#ec4899',
    },
  ];

  // Auto-cycle through scenes
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentScene((prev) => (prev + 1) % scenes.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [scenes.length]);

  const scene = scenes[currentScene];
  const Icon = scene.icon;

  return (
    <div className="relative w-full max-w-2xl mx-auto mb-8">
      {/* Scene indicators */}
      <div className="flex justify-center gap-2 mb-6">
        {scenes.map((_, idx) => (
          <button
            key={idx}
            onClick={() => setCurrentScene(idx)}
            className="relative h-1.5 rounded-full transition-all duration-300 overflow-hidden"
            style={{ 
              width: idx === currentScene ? '32px' : '12px',
              background: idx === currentScene ? LANDING_COLORS.primary : 'rgba(255,255,255,0.2)',
            }}
          >
            {idx === currentScene && (
              <motion.div
                className="absolute inset-0 rounded-full"
                style={{ background: LANDING_COLORS.primary }}
                initial={{ scaleX: 0, originX: 0 }}
                animate={{ scaleX: 1 }}
                transition={{ duration: 4, ease: 'linear' }}
              />
            )}
          </button>
        ))}
      </div>

      {/* Scene content */}
      <AnimatePresence mode="wait">
        <motion.div
          key={currentScene}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.5 }}
          className="text-center"
        >
          {/* Icon with glow */}
          <motion.div
            className="w-16 h-16 mx-auto mb-4 rounded-2xl flex items-center justify-center"
            style={{ background: `${scene.color}20` }}
            animate={{ scale: [1, 1.1, 1] }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            <Icon className="w-8 h-8" style={{ color: scene.color }} />
          </motion.div>

          {/* Main text */}
          <h3 className="text-2xl md:text-3xl font-bold text-white mb-2">
            {scene.text}
          </h3>
          
          {/* Subtext */}
          <p className="text-lg text-white/60">
            {scene.subtext}
          </p>
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
